import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Check, Sparkles, Minus, Plus, Zap, Flame, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

// ---------- Subscription tiers (exact spec copy) ----------
const TIERS = [
    {
        id: 'free', name: 'Free Tier', monthly: 0, annual: 0, save: 0,
        credits: '20 Credits / month',
        creditsNote: 'Refreshes monthly; non-stackable',
        audience: 'Curious drop-ins & casual users testing PDF rendering and AI features.',
        features: [
            'Standard digital journal flip view',
            'Basic text-to-notes (up to 3 pages)',
            'Basic study dashboard',
            'Single-subject notebook creation',
            'Standard rendering speed (10MB file limit)',
        ],
        usage: '20-page PDF conversion (20 credits) OR 4 AI Q&A answers (20 credits).',
        cta: 'Get Started Free',
    },
    {
        id: 'base', name: 'Base Plan', monthly: 200, annual: 1400, save: 1000,
        credits: '150 Credits / month',
        audience: 'Budget-conscious high school & undergrad students managing 1–2 challenging subjects.',
        features: [
            'Full PDF/Journal flipbook conversion',
            'Standard flashcard & quiz generation',
            'Up to 3 active subject folders',
            'Standard PDF/image exports (25MB file limit)',
        ],
        usage: '100-page PDF conversion (100 credits) + 5 AI Summaries (25 credits) + 5 Q&A responses (25 credits).',
        cta: 'Subscribe Now',
    },
    {
        id: 'core', name: 'Core Scholar', badge: 'MOST POPULAR', badgeIcon: 'star',
        monthly: 350, annual: 2200, save: 2000,
        credits: '300 Credits / month',
        audience: 'Regular college students with weekly assignments, heavy readings, and continuous study routines.',
        features: [
            'Everything in Base Plan',
            'Multi-device sync',
            'Advanced AI flashcard decks & formula recognition',
            'Key takeaway extraction',
            'Unlimited subject folders',
            'Standard AI tutor chat (50MB file limit)',
        ],
        usage: '150-page PDF conversion (150 credits) + 5 Flashcard Decks (50 credits) + 5 Mock Tests (50 credits) + 10 Q&A answers (50 credits).',
        cta: 'Get Core Scholar',
    },
    {
        id: 'pro', name: 'Pro Deal', badge: '🔥 Best Value', badgeIcon: 'flame',
        monthly: 500, annual: 4000, save: 2000,
        credits: '650 Credits / month',
        audience: 'Hardcore crammers, medical, engineering, or law students managing massive text volumes.',
        features: [
            'Everything in Core Scholar',
            'Live Canvas Math Solving Included',
            'Highest credit-to-rupee ratio (< ₹0.77/credit)',
            'Priority AI speed queue',
            'HD vector PDF exports',
            'Exam Prep Simulator',
            'Unused credits roll over once (up to 300 max)',
        ],
        usage: 'Daily heavy study across large textbooks, continuous AI summaries, multi-chapter flashcards, and weekly full mock test generations.',
        cta: 'Upgrade to Pro',
    },
    {
        id: 'mega', name: 'Mega Saver', monthly: 1500, annual: 16000, save: 2000,
        credits: '1,000 Credits / month',
        audience: 'Intensive competitive exam prep (UPSC, GATE, CA) or study group leads needing massive headroom.',
        features: [
            'Everything in Pro Deal',
            'Live Canvas Math Solving Included',
            'Dedicated high-volume server priority',
            'Extended context window for massive PDF uploads (up to 150MB limit)',
        ],
        usage: 'High-volume exam cramming across multiple heavy textbooks and intensive daily AI tutoring.',
        cta: 'Get Mega Saver',
    },
    {
        id: 'unlimited', name: 'Unlimited Pass', badge: '⚡ Power Scholar', badgeIcon: 'zap',
        monthly: 2500, annual: 28000, save: 2000,
        credits: 'Unlimited Tokens',
        audience: 'Power researchers, content creators, and affluent students wanting zero credit anxiety.',
        features: [
            'Zero credit limits across all tools',
            'Live Canvas Math Solving Included',
            'Priority cloud queue',
            'Custom branding',
            'High-res exports',
            'Batch PDF parsing',
            'Early feature access (Fair-use human policy applies; automated botting prohibited)',
        ],
        usage: 'Unbounded research, publishing-grade exports, and always-on AI mentorship.',
        cta: 'Go Unlimited',
    },
];

const PACK_RATE = 60;      // ₹ per subject / month
const PACK_CREDITS = 100;  // credits per subject / month
const BOOST_PRICE = 100;   // +₹100
const BOOST_CREDITS = 60;  // +60 credits

export default function Pricing() {
    const [cycle, setCycle] = useState('monthly');
    const [email, setEmail] = useState('');
    const [order, setOrder] = useState(null);
    const [checkingOut, setCheckingOut] = useState(null);

    // Custom Subject Pack calculator state
    const [subCount, setSubCount] = useState(2);
    const [booster, setBooster] = useState(false);
    const packTotal = subCount * PACK_RATE + (booster ? BOOST_PRICE : 0);
    const packCredits = subCount * PACK_CREDITS + (booster ? BOOST_CREDITS : 0);

    async function mockCheckout(plan, amount) {
        if (amount > 0 && !email) { alert('Please enter your email above to continue.'); return; }
        setCheckingOut(plan);
        try {
            if (amount === 0) { setOrder({ plan, free: true }); return; }
            const res = await axios.post(`${API}/razorpay/order`, { amount, plan, email: email || 'guest@studyfolio.in' });
            setOrder({ ...res.data, plan });
        } catch (e) { console.error(e); } finally { setCheckingOut(null); }
    }

    return (
        <div data-testid="section-pricing" className="space-y-10">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§V — Pricing (INR)</div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">Pay in ₹ · UPI / Cards</div>
            </div>

            {/* Header + billing toggle */}
            <div>
                <h2 className="display text-[color:var(--ink)]" style={{ fontSize: 'clamp(34px, 4.2vw, 56px)' }}>
                    Flexible Plans for <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>Every Scholar</span>
                </h2>
                <p className="text-[color:var(--ink-soft)] text-[15px] mt-3 max-w-xl">
                    Select a monthly plan or switch to annual billing for instant savings up to ₹2,000/year.
                </p>
                <div className="flex items-center justify-between gap-4 flex-wrap mt-5">
                    <input
                        data-testid="checkout-email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border-b border-[color:var(--moss-deep)] bg-transparent outline-none px-1 py-1 text-sm text-[color:var(--ink)] placeholder:text-[color:var(--moss-deep)]/50 w-56"
                    />
                    <div className="inline-flex items-center rounded-full border border-[color:var(--moss-deep)] p-1 bg-white/40" data-testid="cycle-toggle">
                        <button onClick={() => setCycle('monthly')}
                            className={`px-4 py-1.5 rounded-full text-[11px] mono transition-colors ${cycle === 'monthly' ? 'bg-[color:var(--lavender)] text-[color:var(--moss-dark)]' : 'text-[color:var(--moss-deep)]'}`}
                            data-testid="cycle-monthly">
                            Monthly Plans
                        </button>
                        <button onClick={() => setCycle('annual')}
                            className={`px-4 py-1.5 rounded-full text-[11px] mono transition-colors ${cycle === 'annual' ? 'bg-[color:var(--lavender)] text-[color:var(--moss-dark)]' : 'text-[color:var(--moss-deep)]'}`}
                            data-testid="cycle-annual">
                            Annual Plans (Save up to ₹2,000)
                        </button>
                    </div>
                </div>
            </div>

            {/* Tier grid */}
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
                {TIERS.map((t, i) => {
                    const amount = cycle === 'annual' ? t.annual : t.monthly;
                    const highlighted = t.id === 'core' || t.id === 'pro';
                    return (
                        <motion.div key={t.id}
                            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ delay: i * 0.06, duration: 0.5 }}
                            className={`relative rounded-sm border p-5 flex flex-col h-full min-w-0 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                                highlighted
                                    ? 'border-[color:var(--lavender-deep)] bg-[color:var(--lavender)]/20 shadow-[0_18px_40px_-18px_rgba(123,94,224,0.55)] hover:shadow-[0_26px_50px_-18px_rgba(123,94,224,0.7)]'
                                    : 'border-[color:var(--moss-deep)]/40 bg-white/40 hover:shadow-[0_18px_36px_-20px_rgba(11,15,7,0.45)] hover:border-[color:var(--moss-deep)]'
                            }`}
                            data-testid={`plan-${t.id}`}>
                            {t.badge && (
                                <div className="absolute -top-2.5 left-5 mono text-[9px] px-2 py-0.5 rounded-sm text-white flex items-center gap-1"
                                    style={{ background: t.id === 'pro' ? '#C2410C' : 'var(--lavender-deep)' }}
                                    data-testid={`badge-${t.id}`}>
                                    {t.badgeIcon === 'star' && <Star size={9} fill="currentColor" />}
                                    {t.badgeIcon === 'flame' && <Flame size={9} fill="currentColor" />}
                                    {t.badgeIcon === 'zap' && <Zap size={9} fill="currentColor" />}
                                    {t.badge}
                                </div>
                            )}
                            <h3 className="display text-[color:var(--ink)] text-2xl mt-2 leading-tight break-words">{t.name}</h3>
                            <div className="mono text-[10px] text-[#7A5FCB] mt-1">{t.credits}</div>
                            {t.creditsNote && <div className="mono text-[9px] text-[color:var(--moss-deep)]/70">{t.creditsNote}</div>}

                            <div className="mt-4 flex items-baseline gap-1.5 flex-wrap">
                                <span className="display text-[color:var(--ink)] tabular-nums leading-none whitespace-nowrap" style={{ fontSize: 'clamp(26px, 2.6vw, 36px)' }}
                                    data-testid={`price-${t.id}`}>
                                    {amount === 0 ? '₹0' : fmt(amount)}
                                </span>
                                <span className="mono text-[10px] text-[color:var(--moss-deep)]">/ {cycle === 'annual' ? 'year' : 'month'}</span>
                                {cycle === 'annual' && t.save > 0 && (
                                    <span className="mono text-[9px] bg-emerald-700/15 text-emerald-800 border border-emerald-700/40 px-1.5 py-0.5 rounded-sm"
                                        data-testid={`save-${t.id}`}>
                                        Save {fmt(t.save)}
                                    </span>
                                )}
                            </div>
                            <div className="display-italic text-[color:var(--ink-soft)] text-[12.5px] mt-2 leading-snug">{t.audience}</div>

                            <ul className="space-y-1.5 mt-4 flex-1">
                                {t.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-[12.5px] text-[color:var(--ink)] leading-snug break-words">
                                        <Check size={13} className="text-[color:var(--lavender-deep)] mt-[3px] flex-shrink-0" />
                                        <span className="min-w-0 flex-1">
                                            {f}
                                            {f.includes('Live Canvas') && (
                                                <span className="relative group inline-flex items-center ml-1.5 align-middle" data-testid={`live-canvas-badge-${t.id}`}>
                                                    <span className="mono text-[8px] tracking-[0.12em] bg-[color:var(--lavender-deep)] text-white px-1.5 py-0.5 rounded-sm cursor-help">
                                                        NEW · AI POWERED
                                                    </span>
                                                    <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 w-60 rounded-sm bg-[#1a1e17] text-[#c2ccbe] mono text-[9px] leading-relaxed px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-xl border border-[#3e4438]">
                                                        Character-by-character math solving synchronized with real-time Qwen voice explanations and interactive pause-and-ask.
                                                    </span>
                                                </span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mono text-[9px] text-[color:var(--moss-deep)]/80 mt-3 pt-3 border-t border-[color:var(--moss-deep)]/15 leading-relaxed">
                                MONTHLY VOLUME — {t.usage}
                            </div>
                            <button data-testid={`checkout-${t.id}`} disabled={checkingOut === t.id}
                                onClick={() => mockCheckout(t.id, amount)}
                                className={`w-full mt-4 py-3 rounded-sm text-xs mono transition-all ${highlighted ? 'btn-lavender' : 'btn-ghost-moss'}`}>
                                {checkingOut === t.id ? 'Preparing…' : t.cta}
                            </button>
                        </motion.div>
                    );
                })}
            </div>

            {/* Custom Subject Pack calculator */}
            <motion.div
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="relative rounded-sm border-2 border-dashed border-[color:var(--lavender-deep)] bg-[color:var(--lavender)]/15 p-6"
                data-testid="plan-custom">
                <div className="absolute -top-2 left-6 mono text-[9px] bg-[color:var(--moss-deep)] text-[color:var(--parchment)] px-2 py-0.5 rounded-sm flex items-center gap-1">
                    <Sparkles size={9} /> A LA CARTE
                </div>
                <h3 className="display text-[color:var(--ink)] text-3xl">Custom Subject Pack</h3>
                <p className="display-italic text-[color:var(--ink-soft)] text-[14px] mt-1 max-w-2xl">
                    For Class 1–9, Class 11, or "Other" learners — pick only the subjects you need without a general monthly plan.
                    {` ${fmt(PACK_RATE)}/month per subject · ${PACK_CREDITS} dedicated credits per subject.`}
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                    {/* Step 1: subject count stepper */}
                    <div>
                        <div className="mono text-[10px] tracking-[0.2em] text-[color:var(--moss-deep)] mb-2">STEP 1 — NUMBER OF SUBJECTS</div>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setSubCount((n) => Math.max(1, n - 1))}
                                data-testid="pack-minus"
                                className="h-9 w-9 rounded-sm border border-[color:var(--moss-deep)]/50 flex items-center justify-center text-[color:var(--ink)] hover:bg-[color:var(--lavender)]/30 transition-colors"
                                aria-label="Remove a subject">
                                <Minus size={14} />
                            </button>
                            <div className="mono text-[12px] text-[color:var(--ink)] min-w-[150px] text-center" data-testid="pack-count">
                                {subCount} Subject{subCount !== 1 ? 's' : ''} Selected
                            </div>
                            <button onClick={() => setSubCount((n) => Math.min(12, n + 1))}
                                data-testid="pack-plus"
                                className="h-9 w-9 rounded-sm border border-[color:var(--moss-deep)]/50 flex items-center justify-center text-[color:var(--ink)] hover:bg-[color:var(--lavender)]/30 transition-colors"
                                aria-label="Add a subject">
                                <Plus size={14} />
                            </button>
                        </div>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-2" data-testid="pack-base">
                            Base: {fmt(subCount * PACK_RATE)} / month ({subCount * PACK_CREDITS} Total Monthly Credits)
                        </div>

                        {/* Step 2: booster */}
                        <div className="mono text-[10px] tracking-[0.2em] text-[color:var(--moss-deep)] mt-6 mb-2">STEP 2 — CREDIT BOOSTER (OPTIONAL)</div>
                        <label className="flex items-center gap-2 text-[13px] text-[color:var(--ink)] cursor-pointer">
                            <input type="checkbox" checked={!booster} onChange={() => setBooster(false)} data-testid="pack-no-boost" />
                            No Top-Up (₹0)
                        </label>
                        <label className="flex items-center gap-2 text-[13px] text-[color:var(--ink)] cursor-pointer mt-1.5">
                            <input type="checkbox" checked={booster} onChange={(e) => setBooster(e.target.checked)} data-testid="pack-boost" />
                            +{BOOST_CREDITS} Extra Credits Boost (+{fmt(BOOST_PRICE)} / month)
                        </label>
                    </div>

                    {/* Live total */}
                    <div className="text-center md:text-right flex flex-col justify-center">
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">ESTIMATED TOTAL</div>
                        <div className="display text-[color:var(--ink)] tabular-nums" style={{ fontSize: 'clamp(40px, 4.6vw, 60px)' }} data-testid="pack-total">
                            {fmt(packTotal)} <span className="text-lg mono text-[color:var(--moss-deep)]">/ month</span>
                        </div>
                        <div className="mono text-[11px] text-[#7A5FCB]" data-testid="pack-credits">{packCredits} Total Monthly Credits</div>
                        <button
                            data-testid="checkout-custom-pack"
                            disabled={checkingOut === 'custom-pack'}
                            onClick={() => mockCheckout('custom-pack', packTotal)}
                            className="btn-lavender px-6 py-3 rounded-sm text-xs mono mt-4 md:ml-auto disabled:opacity-40">
                            {checkingOut === 'custom-pack' ? 'Preparing…' : 'Build My Pack & Subscribe →'}
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Mock checkout confirmation */}
            {order && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                    className="border border-[color:var(--lavender-deep)] rounded-sm p-4 bg-[color:var(--lavender)]/20" data-testid="checkout-session">
                    <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                        {order.free ? 'FREE PLAN ACTIVATED' : 'RAZORPAY ORDER (MOCK)'}
                    </div>
                    <div className="text-sm mt-1 text-[color:var(--ink)]">
                        {order.free ? (
                            <>You're on the <b>Free Tier</b> — start reading right away.</>
                        ) : (
                            <>Order <span className="mono">{order.order_id?.slice(0, 18)}</span> for <b>{order.plan}</b> created.
                                <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-1">
                                    Status: {order.status} · MOCKED Razorpay (no real charge). Add test keys to go live with UPI/Cards/NetBanking.
                                </div>
                            </>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
}
